/**
 * Shoppable Videos snippet-author runtime.
 *
 * Loaded via async <script src> from each render of the snippet's main
 * .liquid. Duplicate requests on multi-instance pages
 * are cache-coalesced; the IIFE is guarded against multi-execution.
 *
 * Responsibilities:
 *   1. Define and register the <sai-fsvfrd01> custom-element class.
 *      Class owns its inner DOM; applyVariant(content, pool) re-renders.
 *   2. Bind each [data-spectrum-instance-id][data-spectrum-snippet-id="fsvfrd01"]
 *      container via __spectrumAi.snippet.bind(node, callback). Bind callback
 *      reads the snippet's bespoke pool and feeds the custom element.
 *
 * Full DOM rebuild on every variant resolution — heading, subheading, preset
 * class, and the entire cards grid + dialog slides + product strips. Pool is
 * the sibling <script type="application/json" data-spectrum-snippet-pool>
 * emitted by the snippet's main .liquid (NOT the wrapper's envelope pool,
 * which is vestigial for this snippet — see plan §15#3 / §15#8).
 *
 * Test surface: when globalThis.__SAI_TEST_HARNESS__ === true, the IIFE
 * exposes globalThis.__saiJzqlk4g5 with DOM string-builders + helpers for
 * unit tests. Production never sets the flag.
 */
;(() => {
  // Multi-execution guard: async <script src> tags emitted per-render mean
  // the IIFE may run multiple times on a page with multiple instances.
  // Custom-element re-define throws; bind iteration would double-register.
  if (window.__sai_fsvfrd01_initialized__) return
  window.__sai_fsvfrd01_initialized__ = true

  const SNIPPET_ID = 'fsvfrd01'
  const TAG = 'sai-fsvfrd01'
  const FEATURE_SLUG = 'shoppable_videos'

  // ── Pool reader ──
  // The snippet emits a sibling <script type="application/json"
  // data-spectrum-snippet-pool> next to the wrapper's envelope inside the
  // outer container. We iterate direct children explicitly (rather than a
  // `:scope > ...` querySelector) for cross-environment reliability —
  // JSDOM doesn't always honour `:scope >` correctly. Direct-child constraint
  // matters: in multi-instance pages, each instance has its own pool, and
  // a nested script (e.g. inside a <sai-fsvfrd01>) should not be picked up.
  function readSnippetPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i]
      if (child.tagName === 'SCRIPT' && child.hasAttribute('data-spectrum-snippet-pool')) {
        const text = child.textContent
        if (!text) return {}
        try {
          return JSON.parse(text)
        } catch {
          return {}
        }
      }
    }
    return {}
  }

  // ── DOM string-builders ──
  // The wrapper Liquid only emits skeleton placeholders + the dialog
  // scaffold + the bespoke pool block (post-Option-3b cutover); these
  // string-builders are the single source of truth for cards, slides, and
  // product cards. Called from applyVariant on init AND on every targeting
  // swap. template-invariants.test.ts pins the few cross-surface
  // invariants that remain (pool shape, async script tag, etc.).

  // Parse a money-formatted string ("$1,699.00", "₹1,699") to a number.
  // Strips everything but digits and the decimal point — currency-agnostic
  // enough for the discount-% math. Returns 0 on empty/unparseable input.
  function parseMoney(str) {
    if (typeof str !== 'string' || str === '') return 0
    const n = Number.parseFloat(str.replace(/[^0-9.]/g, ''))
    return Number.isFinite(n) ? n : 0
  }

  function buildPlaySvg() {
    // Figma "See it in Action" card: a bare black play triangle (no chrome),
    // centred. The wrapper CSS adds a soft shadow so it stays legible over a
    // photographic poster where the mock's flat grey placeholder didn't need one.
    return (
      '<svg width="26" height="26" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M5.5 3.5L14.5 9L5.5 14.5V3.5Z" fill="#131313"/>' +
      '</svg>'
    )
  }

  // buildCard branches by cardStyle:
  //   plain   — flat: <button>{media}{playOverlay}</button>. Identical to the
  //             original svmk8tqx layout; preserves CSS for __card-poster /
  //             __card-video / __play without wrapping changes.
  //   stacked — <button><div.__card-media>{media}{playOverlay}</div>
  //                     {listProductCard --stacked}</button>. Product card
  //             renders as a sibling below the media; CSS sets the card to
  //             flex-direction: column with a small gap.
  //   overlay — same DOM as stacked with the --overlay modifier on the
  //             product card; CSS positions the product card absolutely
  //             over the media as a white-bg pill near the bottom.
  // Either way the whole tile is one <button> = one tap target. The product
  // info inside is aria-hidden so screen readers announce only the button's
  // "Play <title>" label; the lightbox surfaces full product context.
  function buildCard(handle, video, index, cardStyle) {
    if (!video) return ''
    const hasMedia = video.video_url || (Array.isArray(video.assets) && video.assets.length > 0)
    if (!hasMedia) return ''
    const title = video.title || 'Video'
    let mediaMarkup
    if (video.poster_url) {
      mediaMarkup = `<img src="${video.poster_url}" alt="${title}" loading="lazy" class="sai-fsvfrd01__card-poster">`
    } else if (video.video_url) {
      const cardSrc = video.preview_url || video.video_url
      mediaMarkup = `<spectrum-video data-src="${cardSrc}" data-autoplay="muted" data-loop data-load="viewport" class="sai-fsvfrd01__card-video"></spectrum-video>`
    } else {
      const firstAsset = video.assets[0]
      mediaMarkup = `<img src="${firstAsset.poster_url || firstAsset.url}" alt="${title}" loading="lazy" class="sai-fsvfrd01__card-poster">`
    }
    const playOverlay = `<div class="sai-fsvfrd01__play" aria-hidden="true"><span class="sai-fsvfrd01__play-btn">${buildPlaySvg()}</span></div>`
    const btnOpen = `<button class="sai-fsvfrd01__card" type="button" role="listitem" data-handle="${handle}" data-index="${index}" aria-label="Play ${title}" aria-haspopup="dialog">`
    if (cardStyle === 'stacked' || cardStyle === 'overlay') {
      const firstTag =
        Array.isArray(video.product_tags) && video.product_tags.length > 0
          ? video.product_tags[0]
          : null
      const productMarkup = buildListProductCard(firstTag, cardStyle)
      return `${btnOpen}<div class="sai-fsvfrd01__card-media">${mediaMarkup}${playOverlay}</div>${productMarkup}</button>`
    }
    return `${btnOpen}${mediaMarkup}${playOverlay}</button>`
  }

  // Listing-surface product card: thumb + title + price for the FIRST tagged
  // product on the video. No CTA / ATC / compare-at — the lightbox is where
  // those live. Marked aria-hidden so screen-reader announcement of the
  // outer button stays clean; visually-displayed text only.
  // No-op when `tag` is falsy (snippet renders the bare video card so the
  // strip never crashes on a video with zero tags).
  function buildListProductCard(tag, cardStyle) {
    if (!tag) return ''
    const variantClass =
      cardStyle === 'overlay'
        ? ' sai-fsvfrd01__card-product--overlay'
        : ' sai-fsvfrd01__card-product--stacked'
    const thumb = tag.thumb_url
      ? `<img class="sai-fsvfrd01__card-product-img" src="${tag.thumb_url}" alt="" width="48" height="48" loading="lazy">`
      : ''
    const price = tag.price_override || tag.variant_price || tag.product_price || ''
    const titleText = tag.display_name || ''
    const priceMarkup = price
      ? `<span class="sai-fsvfrd01__card-product-price">${price}</span>`
      : ''
    return `<div class="sai-fsvfrd01__card-product${variantClass}" aria-hidden="true"><div class="sai-fsvfrd01__card-product-info">${thumb}<div class="sai-fsvfrd01__card-product-text"><span class="sai-fsvfrd01__card-product-title">${titleText}</span>${priceMarkup}</div></div></div>`
  }

  // Inert grid slot for refIds whose pool entry is missing or has no
  // video_url. Shares the __card class so width + aspect-ratio are
  // reserved → no CLS when the JS swaps an SSR placeholder to either a
  // real card or an unavailable indicator. CSS handles the visible
  // "Video unavailable" label via ::after.
  function buildUnavailable(handle) {
    return `<div class="sai-fsvfrd01__card sai-fsvfrd01__card--unavailable" data-handle="${handle}" aria-hidden="true"></div>`
  }

  // Per-asset duration when the asset is an image (Stories pacing for non-video content).
  const IMAGE_DISPLAY_MS = 5000

  // Story-aware dialog slide. Each video has ONE OR MORE assets (Instagram-Stories
  // pattern): the slide stacks vertically — progress bar at the top, story media
  // in the middle (one __story-asset per asset, only the active one visible), our
  // multi-product strip pinned to the bottom. JS cycles through assets via
  // _storyGo(±delta); overflow at first/last asset moves to the prev/next slide.
  //
  // For legacy single-video data (no assets[]), we synthesise a one-asset array
  // from top-level video_url/poster_url so the slide still renders during the
  // transition. Once data is fully migrated, this fallback is dormant.
  //
  // Empty product_tags → omit the strip entirely (degraded mode rather than a
  // stub overlay; the video still plays).
  function buildDialogSlide(handle, video, index, total, ctaText, ctaAction) {
    if (!video) return ''
    const assets =
      Array.isArray(video.assets) && video.assets.length > 0
        ? video.assets
        : video.video_url
          ? [{ type: 'video', url: video.video_url, poster_url: video.poster_url }]
          : []
    if (assets.length === 0) return ''
    const multi = assets.length > 1

    // Group-level progress bar: ONE segment per video in the group (live), not
    // per asset. Every slide renders the full bar; the JS fills segments before
    // this video's index and animates the segment AT this video's index
    // (data-seg = the group position), so the bar reads as group progress.
    // Live: a single POSITION bar (black fill on a light track) — fill width =
    // 1/count anchored at this slide's index. Static per slide: it shows where
    // you are in the video group, not playback time. The old per-segment
    // time-fill machinery no-ops safely (its selectors match nothing).
    const segCount = Number.isFinite(total) && total > 0 ? total : 1
    const posPct = 100 / segCount
    const progressBar = `<div class="sai-fsvfrd01__story-bar"><div class="sai-fsvfrd01__story-track"><div class="sai-fsvfrd01__story-pos" style="width:${posPct}%;left:${index * posPct}%"></div></div></div>`

    let assetMarkup = ''
    for (let ai = 0; ai < assets.length; ai++) {
      const a = assets[ai]
      const hidden = ai > 0 ? ' hidden' : ''
      if (a.type === 'image') {
        assetMarkup += `<div class="sai-fsvfrd01__story-asset" data-asset-idx="${ai}" data-asset-type="image"${hidden}><img src="${a.url}" alt="" loading="lazy" class="sai-fsvfrd01__story-img"></div>`
      } else {
        const pAttr = a.poster_url ? ` data-poster="${a.poster_url}"` : ''
        const pImg = a.poster_url ? `<img src="${a.poster_url}" alt="" loading="lazy">` : ''
        const loopAttr = multi ? '' : ' data-loop'
        assetMarkup += `<div class="sai-fsvfrd01__story-asset" data-asset-idx="${ai}" data-asset-type="video"${hidden}><spectrum-video data-src="${a.url || ''}"${pAttr} data-autoplay="muted"${loopAttr} data-load="click" class="sai-fsvfrd01__dialog-video">${pImg}</spectrum-video></div>`
      }
    }

    const tapZones =
      '<div class="sai-fsvfrd01__story-tap sai-fsvfrd01__story-tap--prev" data-action="story-prev"></div><div class="sai-fsvfrd01__story-tap sai-fsvfrd01__story-tap--next" data-action="story-next"></div>'

    const tags = Array.isArray(video.product_tags) ? video.product_tags : []
    const cards = tags
      .map((tag) => buildProductCard(tag, ctaText, ctaAction))
      .filter(Boolean)
      .join('')
    const stripHtml = cards
      ? `<div class="sai-fsvfrd01__dialog-products"><div class="sai-fsvfrd01__dialog-products-scroll">${cards}</div></div>`
      : ''
    // First slide carries data-mactive at BUILD time so the (mobile-fixed)
    // progress bar + product strip show immediately, even before the scroll
    // observer runs — otherwise the CSS `:not([data-mactive])` gate would hide
    // ALL chrome until JS marks one active.
    const mactiveAttr = index === 0 ? ' data-mactive="1"' : ''
    return `<div class="sai-fsvfrd01__dialog-slide" data-handle="${handle}" data-index="${index}" data-position="hidden"${mactiveAttr} data-asset-count="${assets.length}" data-active-asset="0">${progressBar}<div class="sai-fsvfrd01__story-media">${assetMarkup}${tapZones}</div>${stripHtml}</div>`
  }

  // Lightbox product card — 3-column grid: image | meta (title + price) | CTA.
  // The CTA branches on cta_action AND variant state:
  //   • product unavailable → "Out of stock" disabled button
  //   • cta_action='redirect' → <a> to the product URL
  //   • single variant → direct add-to-cart (cta-atc-direct), regardless of
  //     cta_action — single-variant skips the mini-PDP because there's
  //     nothing to pick.
  //   • multi-variant + cta_action='mini_pdp' → opens the bottom sheet.
  // Title / image taps are always anchors with data-action="product-click",
  // so the analytics path stays consistent with the dialog click delegate.
  function buildProductCard(t, ctaText, ctaAction) {
    if (!t) return ''
    const variants = Array.isArray(t.variants) ? t.variants : []
    const availableVariants = variants.filter((v) => v?.available)
    // Availability rule:
    //   - top-level `available !== false` is required; merchants can hard-flag
    //     a product as OOS even when individual variants look fine
    //   - if variants[] is non-empty, AT LEAST one variant must be available
    //   - if variants[] is empty (legacy pool entries that pre-date the
    //     variants[] field), trust the top-level signal alone
    const variantsKnown = variants.length > 0
    const productAvailable =
      t.available !== false && (!variantsKnown || availableVariants.length > 0)
    const productUnavailable = !productAvailable

    const tagIdAttr = t.tag_id != null ? ` data-tag-id="${t.tag_id}"` : ''
    const productIdAttr = t.product_id != null ? ` data-product-id="${t.product_id}"` : ''
    const handleAttr = t.handle ? ` data-product-handle="${t.handle}"` : ''
    const productHref = t.cta_url || t.product_url || '#'

    let actionHtml
    if (productUnavailable) {
      actionHtml = `<button class="sai-fsvfrd01__cta" type="button" disabled>Out of stock</button>`
    } else if (ctaAction === 'redirect') {
      actionHtml = `<a class="sai-fsvfrd01__cta" href="${productHref}" data-action="product-click">${ctaText}</a>`
    } else if (variants.length <= 1) {
      // 0 or 1 variant: direct ATC. Pool entries authored before the
      // variants[] field landed fall back to t.variant_id (the pre-selected
      // variant). cta_action='mini_pdp' on a single-variant product still
      // does direct ATC — there's nothing for the merchant to pick.
      const variantId = variants.length === 1 ? variants[0].id : t.variant_id
      actionHtml = `<button class="sai-fsvfrd01__cta" type="button" data-action="cta-atc-direct" data-variant-id="${variantId}"${handleAttr}${tagIdAttr}${productIdAttr}>${ctaText}</button>`
    } else {
      actionHtml = `<button class="sai-fsvfrd01__cta" type="button" data-action="cta-quickshop"${handleAttr}${tagIdAttr}${productIdAttr}>${ctaText}</button>`
    }

    const titleHtml = `<a href="${productHref}" class="sai-fsvfrd01__product-title" data-action="product-click">${t.display_name || ''}</a>`
    // Live's card stacks a struck MRP (compare-at) above the sale price. The
    // pool only emits compare_at_price when it exceeds the current price, so a
    // full-price product simply omits the struck line.
    // Live shows whole-rupee amounts ("₹11,999", not "₹11,999.00").
    const tidyMoney = (s) => (typeof s === 'string' ? s.replace(/\.00(?!\d)/, '') : s)
    const priceNow = tidyMoney(t.price_override || t.variant_price || t.product_price || '')
    const priceMrp = tidyMoney(t.compare_at_price || '')
    const priceHtml = priceNow
      ? `<p class="sai-fsvfrd01__product-price">${
          priceMrp
            ? `<span class="sai-fsvfrd01__product-price-mrp">MRP: ${priceMrp}</span>`
            : ''
        }<span class="sai-fsvfrd01__product-price-now">${priceNow}</span></p>`
      : ''
    const imageHtml = t.thumb_url
      ? `<a href="${productHref}" class="sai-fsvfrd01__product-image-wrap" tabindex="-1" aria-hidden="true" data-action="product-click"><img class="sai-fsvfrd01__product-image" src="${t.thumb_url}" alt="" loading="lazy" width="60" height="60"></a>`
      : `<span class="sai-fsvfrd01__product-image-wrap" aria-hidden="true"></span>`

    // Live layout: white info card = thumb | title (left) | price block (right,
    // MRP struck over the sale price), with the CTA as a SEPARATE full-width bar
    // below it. The CTA keeps its own data-* attrs so its handlers never depend
    // on DOM ancestry; only the title/image product-click uses closest('__product-card').
    return `<div class="sai-fsvfrd01__product-block"${handleAttr}${tagIdAttr}${productIdAttr}><div class="sai-fsvfrd01__product-card">${imageHtml}<div class="sai-fsvfrd01__product-meta">${titleHtml}</div>${priceHtml}</div>${actionHtml}</div>`
  }

  // ── Quickshop helpers (pure) ──
  // Match a variant whose options[] aligns with the selected values by name.
  // Returns the variant or null. Positional under the assumption that
  // tag.options[i] and variant.options[i] are aligned (Shopify convention).
  function findVariantByOptions(tag, optionValues) {
    const variants = Array.isArray(tag.variants) ? tag.variants : []
    const opts = Array.isArray(tag.options) ? tag.options : []
    for (const v of variants) {
      const vOpts = Array.isArray(v.options) ? v.options : []
      let hit = true
      for (let i = 0; i < opts.length; i++) {
        if (vOpts[i] !== optionValues[opts[i].name]) {
          hit = false
          break
        }
      }
      if (hit) return v
    }
    return null
  }

  // Pre-select the merchant-pinned target variant when present, else the
  // first available variant, else the first variant period. The picked
  // variant seeds the option-pill state at openQuickshop.
  function pickInitialVariant(tag) {
    const variants = Array.isArray(tag.variants) ? tag.variants : []
    if (tag.variant_id) {
      const target = variants.find((v) => String(v.id) === String(tag.variant_id))
      if (target) return target
    }
    return variants.find((v) => v?.available) || variants[0] || null
  }

  function optionValuesFromVariant(tag, variant) {
    const result = {}
    const opts = Array.isArray(tag.options) ? tag.options : []
    const vOpts = Array.isArray(variant?.options) ? variant.options : []
    opts.forEach((opt, i) => {
      result[opt.name] = vOpts[i] ?? ''
    })
    return result
  }

  // Is there any AVAILABLE variant where (optName=optValue) AND every other
  // currently-selected option holds? Used to dim incompatible pills as the
  // user picks their way through option groups.
  function isOptionValueAvailable(tag, optName, optValue, currentValues) {
    const variants = Array.isArray(tag.variants) ? tag.variants : []
    const opts = Array.isArray(tag.options) ? tag.options : []
    for (const v of variants) {
      if (!v.available) continue
      const vOpts = Array.isArray(v.options) ? v.options : []
      let hit = true
      for (let i = 0; i < opts.length; i++) {
        const name = opts[i].name
        const target = name === optName ? optValue : currentValues[name]
        if (target != null && target !== '' && vOpts[i] !== target) {
          hit = false
          break
        }
      }
      if (hit) return true
    }
    return false
  }

  function buildQuickshopPill(opt, ov) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'sai-fsvfrd01__quickshop-pill'
    btn.setAttribute('role', 'radio')
    btn.dataset.optionName = opt.name
    btn.dataset.optionValue = ov.name
    const isSwatchColor = !!ov.swatch_color
    const isSwatchImage = !!ov.swatch_image
    if (isSwatchColor) btn.classList.add('sai-fsvfrd01__quickshop-pill--swatch-color')
    if (isSwatchImage) btn.classList.add('sai-fsvfrd01__quickshop-pill--swatch-image')
    let inner = ''
    if (isSwatchImage) {
      inner += `<span class="sai-fsvfrd01__quickshop-pill-swatch" style="background-image:url('${ov.swatch_image}')"></span>`
    } else if (isSwatchColor) {
      inner += `<span class="sai-fsvfrd01__quickshop-pill-swatch" style="background-color:${ov.swatch_color}"></span>`
    }
    inner += `<span class="sai-fsvfrd01__quickshop-pill-label">${ov.name}</span>`
    btn.innerHTML = inner
    return btn
  }

  // ── Custom element ──
  if (!customElements.get(TAG)) {
    class SaiShoppableVideos extends HTMLElement {
      // Parser-inserted custom elements whose class is already registered get
      // upgraded at the start tag — before children are parsed. Wait for the
      // subtree before running setup that queries descendants. Anchor on the
      // dialog's close button: deep enough that its presence implies the rest
      // of the subtree is in place.
      connectedCallback() {
        if (this._initialized) return
        const ready = () => !!this.querySelector('.sai-fsvfrd01__dialog-close')
        if (ready()) {
          this._init()
          return
        }
        const obs = new MutationObserver(() => {
          if (ready()) {
            obs.disconnect()
            this._init()
          }
        })
        obs.observe(this, { childList: true, subtree: true })
      }

      _init() {
        this._initialized = true

        this.dialog = this.querySelector('.sai-fsvfrd01__dialog')
        this.slidesScroller = this.querySelector('.sai-fsvfrd01__dialog-slides')
        this.muteBtn = this.querySelector('.sai-fsvfrd01__dialog-mute')
        this.muteIconMuted = this.querySelector('.sai-fsvfrd01__mute-icon--muted')
        this.muteIconSound = this.querySelector('.sai-fsvfrd01__mute-icon--sound')
        this.prevBtn = this.querySelector('[data-action="prev"]')
        this.nextBtn = this.querySelector('[data-action="next"]')
        this.gridEl = this.querySelector('.sai-fsvfrd01__grid')
        this.desktopMQ = window.matchMedia('(min-width: 1024px)')

        // Carousel controls — live in the persistent header/pager scaffold
        // (NOT rebuilt by applyVariant), so query once here. Desktop shows the
        // header arrows; mobile shows the pager (dots + arrows). Both dispatch
        // the same carousel-prev / carousel-next actions.
        this.navEl = this.querySelector('.sai-fsvfrd01__nav')
        this.navPrev = this.querySelector('.sai-fsvfrd01__nav-btn--prev')
        this.navNext = this.querySelector('.sai-fsvfrd01__nav-btn--next')
        this.pagerEl = this.querySelector('.sai-fsvfrd01__pager')
        this.dotsEl = this.querySelector('.sai-fsvfrd01__dots')
        this.pagerPrev = this.querySelector('.sai-fsvfrd01__pager-btn--prev')
        this.pagerNext = this.querySelector('.sai-fsvfrd01__pager-btn--next')

        // CTA settings — read once from data-attrs on the host. The wrapper
        // re-renders on variant resolution by replacing the whole subtree, so
        // changes to cta_text / cta_action between variants flow through via
        // applyVariant reading the current dataset.
        this._ctaText = this.dataset.ctaText || 'Shop now'
        this._ctaAction = this.dataset.ctaAction === 'mini_pdp' ? 'mini_pdp' : 'redirect'

        // Quickshop (mini-PDP bottom sheet) refs + state.
        this.quickshopEl = this.querySelector('.sai-fsvfrd01__quickshop')
        this.qsState = {
          variantId: '',
          handle: '',
          tag: null,
          optionValues: {},
          hasOptions: false,
        }
        this._qsCloseTimer = null

        this.activeIndex = 0
        this.observer = null
        // Audio preference scoped to the open dialog. Resets on close so
        // every dialog open starts muted (autoplay-safe). `toggleMute` is
        // the only writer while the dialog is open.
        this.audioOn = false

        // ── Analytics state ──
        // Set later via setAnalytics(track, emit) by bindAllContainers. Until
        // then, _track/_emit are no-ops so the snippet works without the
        // bootstrap SDK loaded (e.g. local dev without analytics).
        this._track = noopTrack
        this._emit = noopEmit
        // `_analyticsReady` gates impression observers from running before
        // the real track/emit handles are installed. Without this gate, an
        // above-the-fold card visible at page-load could fire an IO callback
        // while track is still noopTrack — silently dropping the impression
        // AND adding the handle to _impressedCards, so the later real track
        // never gets a chance to fire it. See review issue #2.
        this._analyticsReady = false
        // Stashed by applyVariant for analytics payload lookups.
        this._pool = {}
        this._refIds = []
        this._cardStyle = 'plain'
        this._listLayout = 'plain'
        // One-shot impression guards — handle Sets so re-applying variant
        // (rebuild) doesn't re-fire impressions for cards already seen.
        this._impressedListFired = false
        this._impressedCards = new Set()
        // Per-dialog-session product-impression dedup (keyed by tag_id).
        // Reset in open(); see _setupProductImpressionsObserver.
        this._impressedProducts = new Set()
        this._cardsObserver = null
        this._productsObserver = null
        this._listObserver = null
        // View-duration state machine.
        // close()/pagehide are unified: pagehide sets _pendingExitReason and
        // calls close(); close() reads the flag (default 'close') so we never
        // double-fire. _viewSession is the active video's session record.
        this._viewSession = null
        this._pendingExitReason = undefined
        // Story state — per-slide asset cycling (Instagram Stories pattern).
        // Per-slide assets advance automatically: video assets end → next;
        // image assets time out after IMAGE_DISPLAY_MS. _clearStoryState
        // tears down the listeners + timers between transitions.
        this._storyImageTimer = null
        this._storyEndedHandler = null
        this._storyPauseHandler = null
        this._storyPlayHandler = null
        this._storyEventTarget = null
        this._storyArmTimer = null

        // Dialog-level delegated listener — survives applyVariant rebuilds
        // because the dialog scaffold (close/mute/nav buttons + quickshop
        // sheet) is preserved.
        this.dialog.addEventListener('click', (e) => {
          // The Size Guide overlay (sai-fsgfrd01) is HOISTED into this dialog so
          // it renders above the mini-PDP. It has its own handlers AND its own
          // `data-action="close"` button — which would otherwise bubble here and
          // match our own close, tearing down the whole drawer. Ignore every
          // click that originates inside it; fsgfrd01 owns those clicks.
          if (e.target.closest?.('.sai-fsgfrd01__root')) return

          // Pill click — most specific case; handle first. Pills carry
          // option-name + option-value; on tap we update qsState.optionValues
          // and re-resolve everything via applySelection().
          const pill = e.target.closest?.('.sai-fsvfrd01__quickshop-pill')
          if (pill && this.quickshopEl && this.quickshopEl.contains(pill)) {
            // OOS pills are inert. Visual cues (line-through + dim) signal
            // it; the click handler hard-blocks selection so the sheet
            // never enters a "no matching variant" state from a tap.
            if (pill.classList.contains('sai-fsvfrd01__quickshop-pill--oos')) return
            const optName = pill.dataset.optionName
            const optValue = pill.dataset.optionValue
            if (optName && this.qsState.tag) {
              this.qsState.optionValues = { ...this.qsState.optionValues, [optName]: optValue }
              this.applyQsSelection(this.qsState.tag)
            }
            return
          }

          if (e.target === this.dialog) return this.close()
          const actionEl = e.target.closest('[data-action]')
          if (!actionEl) return
          const action = actionEl.dataset.action
          if (action === 'close') this.close()
          else if (action === 'mute') this.toggleMute()
          else if (action === 'share') this._shareCurrent()
          else if (action === 'prev') this._storyGo(-1)
          else if (action === 'next') this._storyGo(+1)
          else if (action === 'story-prev') {
            e.stopPropagation()
            this._storyGo(-1)
          } else if (action === 'story-next') {
            e.stopPropagation()
            this._storyGo(+1)
          } else if (action === 'product-click') this._fireProductClick(actionEl)
          else if (action === 'cta-quickshop') {
            e.preventDefault()
            // Prefer the global mini-PDP popup (real Studio picker instances hosted in a
            // theme <dialog>) when the theme provides it; fall back to the built-in quickshop.
            if (document.querySelector('.frido-mini-pdp__dialog')) {
              window.dispatchEvent(new CustomEvent('spectrum:mini-pdp:open', { detail: { source: SNIPPET_ID } }))
            } else {
              this.openQuickshop(this._findTagOnCurrentSlide(actionEl.dataset.tagId))
            }
          } else if (action === 'cta-atc-direct') {
            e.preventDefault()
            const variantId = actionEl.dataset.variantId
            if (variantId) this.performAtc(variantId, 1, actionEl)
          } else if (action === 'qs-sizeguide') {
            e.preventDefault()
            this._openSizeGuide()
          } else if (action === 'qs-close') {
            this.closeQuickshop()
          } else if (action === 'qs-atc' && !actionEl.disabled) {
            const variantId = this.qsState.variantId
            if (!variantId) return
            // Defensive — re-resolve against the live variant list to catch
            // a programmatic / keyboard flow that flipped state without
            // running through applyQsSelection's availability gate.
            const tag = this.qsState.tag
            const variant = (tag?.variants || []).find((v) => String(v.id) === String(variantId))
            if (!variant || !variant.available) {
              actionEl.disabled = true
              return
            }
            this.performAtc(variantId, 1, actionEl)
          }
        })

        // Escape / Android-back / browser back-gesture fire the dialog's
        // `cancel`. When the mini-PDP is open, that must close ONLY the
        // mini-PDP (back to the video), NOT the whole drawer — otherwise
        // "closing the size sheet" nukes the entire modal. preventDefault
        // keeps the <dialog> open; we collapse the sheet ourselves.
        this.dialog.addEventListener('cancel', (e) => {
          if (this.quickshopEl?.classList.contains('sai-fsvfrd01__quickshop--open')) {
            e.preventDefault()
            this.closeQuickshop()
            return
          }
          this.close()
        })

        // List-level swipe scroll → `shoppable_videos:list_scroll_elem_clicked`. Debounced
        // so a single discrete swipe gesture fires once. `scroll_direction`
        // is derived from `scrollLeft` delta vs the value at the start of
        // the gesture window.
        this._lastGridScrollLeft = 0
        this._gridScrollDebounceTimer = null
        if (this.gridEl) {
          this._lastGridScrollLeft = this.gridEl.scrollLeft
          this.gridEl.addEventListener('scroll', () => this._onGridScroll(), { passive: true })
        }

        // Recompute control visibility whenever the rail's box changes: a
        // viewport resize can flip a rail between "overflows" and "all fits",
        // and a section that starts collapsed (clientWidth 0 at build time)
        // only reports a real width once it lays out. ResizeObserver covers
        // both and fires an initial callback on observe().
        if (this.gridEl && typeof ResizeObserver !== 'undefined') {
          this._gridResizeObserver = new ResizeObserver(() => {
            this._syncCarouselOverflow()
            this._updateCarouselUi()
          })
          this._gridResizeObserver.observe(this.gridEl)
        }

        // Carousel prev/next — one delegated listener on the host covers both
        // the desktop header arrows and the mobile pager arrows. Cards carry
        // their own click handlers (added in _refresh) and are never inside a
        // nav button, so there is no dispatch collision.
        this.addEventListener('click', (e) => {
          const btn = e.target.closest(
            '[data-action="carousel-prev"],[data-action="carousel-next"]',
          )
          if (!btn || !this.contains(btn)) return
          e.preventDefault()
          this._scrollCarousel(btn.dataset.action === 'carousel-next' ? 1 : -1)
        })

        // Close on navigation away (product link, View Cart, external links,
        // back/forward). `pagehide` fires before the page is hidden or put
        // into bfcache — closing here ensures a back-restore doesn't bring
        // the dialog back open.
        //
        // Analytics: route `view_duration` exit-reason through close() so the
        // single close path emits exactly once. See §5.5 of the brainstorming
        // doc — _pendingExitReason is the only place 'pagehide' is set.
        this._onPageHide = () => {
          if (this.dialog?.open) {
            this._pendingExitReason = 'pagehide'
            this.close()
          }
        }
        window.addEventListener('pagehide', this._onPageHide)

        // Initial DOM-ref capture for cards/slides/product-scrolls (these are
        // re-queried on every applyVariant rebuild).
        this._refresh()
      }

      /**
       * Receive the analytics handles from `__spectrumAi.snippet.bind(...)`.
       * Called once per page-load by `bindAllContainers`. After this:
       *  - `_analyticsReady` is set; impression observers can now fire.
       *  - List impression observer is set up (fires once at ≥50% viewport).
       *  - Per-card impression observer is set up (re-bound on every applyVariant
       *    rebuild via _refresh).
       */
      setAnalytics(track, emit) {
        this._track = typeof track === 'function' ? track : noopTrack
        this._emit = typeof emit === 'function' ? emit : noopEmit
        this._analyticsReady = true
        this._setupListImpressionObserver()
        // _refresh has already run (at _init / applyVariant); rerun the
        // per-card observer setup now that analytics is ready. Subsequent
        // applyVariant rebuilds re-call _refresh which re-binds the observer
        // (and at that point _analyticsReady is already true).
        this._setupCardImpressionsObserver()
      }

      // Re-query DOM refs that change on rebuild (cards, slides, product
      // scrollers) and re-attach card click handlers. Idempotent; called
      // from _init AND from applyVariant.
      //
      // Excludes placeholders and unavailable indicators — only real cards
      // (those backed by a pool entry with video_url) get listeners. Both
      // modifiers also have pointer-events:none, so this is belt-and-braces.
      _refresh() {
        this.slides = Array.from(this.querySelectorAll('.sai-fsvfrd01__dialog-slide'))
        this.cards = Array.from(
          this.querySelectorAll(
            '.sai-fsvfrd01__card:not(.sai-fsvfrd01__card--placeholder):not(.sai-fsvfrd01__card--unavailable)',
          ),
        )
        this.productScrolls = Array.from(
          this.querySelectorAll('.sai-fsvfrd01__dialog-products-scroll'),
        )

        // Cards have per-card click handlers (legacy pattern). Old cards are
        // removed via innerHTML replacement so their listeners GC; new cards
        // need fresh handlers. cards[i] index == slide index (real cards
        // and slides are emitted in lockstep by applyVariant).
        //
        // Analytics: fire `shoppable_videos:preview_click_elem_clicked` (track + emit)
        // before opening the dialog. The DOM emit lets theme code react
        // to dialog opens (e.g. pause an autoplaying hero video).
        for (let i = 0; i < this.cards.length; i++) {
          const cardIndex = i
          const card = this.cards[i]
          card.addEventListener('click', () => {
            const handle = card.dataset.handle
            const payload = handle ? { video_id: handle } : {}
            this._track(`${FEATURE_SLUG}:preview_click_elem_clicked`, payload)
            this._emit(`${FEATURE_SLUG}:preview_click_elem_clicked`, payload)
            this.open(cardIndex)
          })
        }

        // Re-bind per-card impression observer for the rebuilt card set.
        this._setupCardImpressionsObserver()
      }

      disconnectedCallback() {
        this._clearStoryState()
        this.observer?.disconnect()
        this._cardsObserver?.disconnect()
        this._productsObserver?.disconnect()
        this._listObserver?.disconnect()
        this._gridResizeObserver?.disconnect()
        this._cardsObserver = null
        this._productsObserver = null
        this._listObserver = null
        this._gridResizeObserver = null
        if (this._gridScrollDebounceTimer) {
          clearTimeout(this._gridScrollDebounceTimer)
          this._gridScrollDebounceTimer = null
        }
        if (this._onPageHide) {
          window.removeEventListener('pagehide', this._onPageHide)
        }
        document.body.style.overflow = ''
      }

      // ── Story asset cycling (Instagram Stories pattern) ──
      //
      // Per-slide asset advance:
      //   • _storyGo(±1) — move within the current slide's assets; overflow
      //     at edges hands off to _goToSlide for prev/next slide transition.
      //     The slide's `data-active-asset` attribute tracks the index.
      //   • _activateStoryAsset(slideIdx, assetIdx) — show the chosen asset
      //     (hide others), start its progress segment, and schedule the
      //     hand-off: video assets call _storyGo(+1) on `ended`; image assets
      //     use setTimeout(IMAGE_DISPLAY_MS).
      //   • _startSegmentAnimation — paints the progress bar's active
      //     segment with an animation matched to the video's `duration` (or
      //     IMAGE_DISPLAY_MS for images). Pause/resume mirror the video.
      //   • _updateStorySegments — sets `data-done` on every segment before
      //     the active one (filled state) and removes `data-active` from
      //     the rest (no animation lingering on outgoing assets).
      //   • _clearStoryState — invalidates timers + listeners and snaps all
      //     of the current slide's segments to filled-static so leaving the
      //     slide doesn't have a half-played animation continuing in the bg.

      _storyGo(delta) {
        const slide = this.slides[this.activeIndex]
        if (!slide) return
        const count = Number.parseInt(slide.dataset.assetCount || '1', 10)
        const cur = Number.parseInt(slide.dataset.activeAsset || '0', 10)
        const next = cur + delta
        if (next >= count) {
          this._goToSlide(this.activeIndex + 1, 0)
          return
        }
        if (next < 0) {
          this._goToSlide(this.activeIndex - 1, -1)
          return
        }
        this._activateStoryAsset(this.activeIndex, next)
      }

      _activateStoryAsset(slideIdx, assetIdx) {
        this._clearStoryState()
        const slide = this.slides[slideIdx]
        if (!slide) return
        slide.dataset.activeAsset = String(assetIdx)

        // The group bar has one segment per video; fill segments before this
        // video and animate the one AT this video's index (slideIdx), not the
        // per-asset index.
        this._updateStorySegments(slide, slideIdx)

        const assetEls = slide.querySelectorAll('.sai-fsvfrd01__story-asset')
        for (let i = 0; i < assetEls.length; i++) {
          const el = assetEls[i]
          const idx = Number.parseInt(el.dataset.assetIdx || '0', 10)
          if (idx === assetIdx) {
            el.removeAttribute('hidden')
            if (el.dataset.assetType === 'video') {
              const vid = el.querySelector('spectrum-video')
              if (vid) {
                vid.load()
                const innerVideo = vid.querySelector('video')
                const eventTarget = innerVideo || vid
                if (innerVideo) {
                  innerVideo.currentTime = 0
                  innerVideo.muted = !this.audioOn
                }
                this._storyEventTarget = eventTarget
                this._storyEndedHandler = () => {
                  this._storyGo(+1)
                }
                const endedHandler = this._storyEndedHandler
                this._storyArmTimer = setTimeout(() => {
                  eventTarget.addEventListener('ended', endedHandler)
                }, 1000)
                vid.play()
                this.applyAudioPreference(vid)
                if (innerVideo) innerVideo.muted = !this.audioOn
                this._startSegmentAnimation(slide, slideIdx, eventTarget)
              }
            } else {
              this._startSegmentAnimation(slide, slideIdx, null)
              this._storyImageTimer = setTimeout(() => {
                this._storyGo(+1)
              }, IMAGE_DISPLAY_MS)
            }
          } else {
            el.setAttribute('hidden', '')
            if (el.dataset.assetType === 'video') {
              const v = el.querySelector('spectrum-video')
              if (v) v.pause()
            }
          }
        }
      }

      _startSegmentAnimation(slide, assetIdx, videoTarget) {
        const fill = slide.querySelector(`[data-seg="${assetIdx}"] .sai-fsvfrd01__story-seg-fill`)
        if (!fill) return

        fill.removeAttribute('data-done')
        fill.removeAttribute('data-active')
        fill.removeAttribute('data-paused')
        fill.style.animationDuration = ''
        fill.style.width = '0%'
        void fill.offsetWidth

        if (videoTarget) {
          let started = false
          function onCanPlay() {
            if (started) return
            started = true
            const dur = videoTarget.duration
            if (dur && Number.isFinite(dur) && dur > 0) {
              fill.style.animationDuration = `${dur}s`
            } else {
              fill.style.animationDuration = '5s'
            }
            fill.setAttribute('data-active', '')
          }
          if (videoTarget.readyState >= 1 && videoTarget.duration > 0) {
            onCanPlay()
          } else {
            videoTarget.addEventListener('loadedmetadata', onCanPlay, { once: true })
            videoTarget.addEventListener('canplay', onCanPlay, { once: true })
          }
          this._storyPauseHandler = () => {
            fill.setAttribute('data-paused', '')
          }
          this._storyPlayHandler = () => {
            fill.removeAttribute('data-paused')
          }
          videoTarget.addEventListener('pause', this._storyPauseHandler)
          videoTarget.addEventListener('playing', this._storyPlayHandler)
        } else {
          fill.style.animationDuration = `${IMAGE_DISPLAY_MS / 1000}s`
          fill.setAttribute('data-active', '')
        }
      }

      _updateStorySegments(slide, activeIdx) {
        const fills = slide.querySelectorAll('.sai-fsvfrd01__story-seg-fill')
        for (let i = 0; i < fills.length; i++) {
          const segIdx = Number.parseInt(fills[i].parentElement.dataset.seg || '0', 10)
          fills[i].removeAttribute('data-active')
          fills[i].removeAttribute('data-paused')
          fills[i].style.animationDuration = ''
          if (segIdx < activeIdx) {
            fills[i].setAttribute('data-done', '')
            fills[i].style.width = '100%'
          } else {
            fills[i].removeAttribute('data-done')
            fills[i].style.width = '0%'
          }
        }
      }

      _clearStoryState() {
        if (this._storyImageTimer) {
          clearTimeout(this._storyImageTimer)
          this._storyImageTimer = null
        }
        const target = this._storyEventTarget
        if (target) {
          if (this._storyEndedHandler) target.removeEventListener('ended', this._storyEndedHandler)
          if (this._storyPauseHandler) target.removeEventListener('pause', this._storyPauseHandler)
          if (this._storyPlayHandler) target.removeEventListener('playing', this._storyPlayHandler)
        }
        if (this._storyArmTimer) {
          clearTimeout(this._storyArmTimer)
          this._storyArmTimer = null
        }
        this._storyEndedHandler = null
        this._storyPauseHandler = null
        this._storyPlayHandler = null
        this._storyEventTarget = null
        const slide = this.slides ? this.slides[this.activeIndex] : null
        if (slide) {
          // Defensive `pause?.()` — disconnectedCallback can fire mid-test
          // before spectrum-video is fully stubbed; the original lifecycle
          // method is unreliable to call directly here.
          const vids = slide.querySelectorAll('spectrum-video')
          for (let vi = 0; vi < vids.length; vi++) vids[vi].pause?.()
          const fills = slide.querySelectorAll('.sai-fsvfrd01__story-seg-fill')
          for (let fi = 0; fi < fills.length; fi++) {
            fills[fi].removeAttribute('data-active')
            fills[fi].removeAttribute('data-paused')
            fills[fi].setAttribute('data-done', '')
            fills[fi].style.animationDuration = ''
            fills[fi].style.width = '100%'
          }
        }
      }

      _initStoryForSlide(slideIdx) {
        const slide = this.slides[slideIdx]
        if (!slide) return
        this._activateStoryAsset(slideIdx, 0)
      }

      // ── Variant resolution entry point (plan §15#8) ──
      // Re-renders the inner DOM from pool data. Called by the bind callback
      // on every $spectrum:variant_resolved event for this instance.
      applyVariant(presentation, slotContent, pool) {
        const content = presentation && typeof presentation === 'object' ? presentation : {}
        const resolvedSlotContent =
          slotContent && typeof slotContent === 'object' && pool !== undefined
            ? slotContent
            : content
        const resolvedPool =
          pool !== undefined
            ? pool
            : slotContent && typeof slotContent === 'object'
              ? slotContent
              : {}
        // Heading / subheading text swap + hidden toggle. The wrapper Liquid
        // always renders the <h2>, <p>, and parent <div> so that experience
        // variants can flip text — including blank ↔ non-blank — without DOM
        // injection. The `hidden` attribute drives visibility; the parent
        // header div hides itself iff BOTH children are hidden.
        const titleEl = this.querySelector('.sai-fsvfrd01__heading')
        const descEl = this.querySelector('.sai-fsvfrd01__subheading')
        const headerEl = this.querySelector('.sai-fsvfrd01__header')
        // Per-product slot title wins over the placement-level heading, mirroring
        // the wrapper Liquid's `slot_content.section_title | default: content.heading`.
        // Without this the client re-render clobbered the SSR'd per-product title
        // with the shared placement heading on every variant resolution.
        const slotTitle =
          resolvedSlotContent && typeof resolvedSlotContent.section_title === 'string'
            ? resolvedSlotContent.section_title
            : null
        const nextHeading =
          slotTitle !== null && slotTitle !== ''
            ? slotTitle
            : typeof content.heading === 'string'
              ? content.heading
              : null
        if (titleEl && nextHeading !== null) {
          titleEl.textContent = nextHeading
          titleEl.toggleAttribute('hidden', nextHeading === '')
        }
        if (descEl && typeof content.subheading === 'string') {
          descEl.textContent = content.subheading
          descEl.toggleAttribute('hidden', content.subheading === '')
        }
        if (headerEl) {
          const titleHidden = titleEl ? titleEl.hasAttribute('hidden') : true
          const descHidden = descEl ? descEl.hasAttribute('hidden') : true
          headerEl.toggleAttribute('hidden', titleHidden && descHidden)
        }

        // Card-style BEM modifier swap on root. Default ('plain') emits no
        // modifier — modifier classes are only present for 'stacked' / 'overlay'.
        const cardStyle =
          content.card_style === 'stacked' || content.card_style === 'overlay'
            ? content.card_style
            : 'plain'
        for (const cls of Array.from(this.classList)) {
          if (cls.startsWith('sai-fsvfrd01--')) this.classList.remove(cls)
        }
        if (cardStyle !== 'plain') {
          this.classList.add(`sai-fsvfrd01--${cardStyle}`)
        }

        // CTA settings — variant content can override the host data-attr
        // defaults (a/b testing the CTA label or action between variants).
        if (typeof content.cta_text === 'string' && content.cta_text !== '') {
          this._ctaText = content.cta_text
        }
        if (content.cta_action === 'mini_pdp' || content.cta_action === 'redirect') {
          this._ctaAction = content.cta_action
        }

        // Stash for analytics payload lookups (list_impression, preview_impression).
        // _listLayout mirrors cardStyle so PostHog dashboards filtering on
        // list_layout see one of plain | stacked | overlay.
        const safePool = resolvedPool && typeof resolvedPool === 'object' ? resolvedPool : {}
        const refIds = Array.isArray(resolvedSlotContent.videoHandles)
          ? resolvedSlotContent.videoHandles
          : (content.videos?.refIds ?? [])
        this._pool = safePool
        this._refIds = refIds
        this._cardStyle = cardStyle
        this._listLayout = cardStyle

        // Total renderable videos — the group-level progress bar has one
        // segment per video, so each slide needs the count up front.
        const totalVideos = refIds.reduce((n, h) => {
          const v = safePool[h]
          return n + (v?.video_url || (Array.isArray(v?.assets) && v.assets.length > 0) ? 1 : 0)
        }, 0)

        const cardItems = []
        const slideItems = []
        let slideIdx = 0
        for (const handle of refIds) {
          const video = safePool[handle]
          // Multi-asset: a video qualifies as having media via the legacy
          // top-level `video_url` (derived from assets[0] in the new pool) or
          // a non-empty `assets[]` array. Either signal builds a real card.
          const hasMedia =
            video?.video_url || (Array.isArray(video?.assets) && video.assets.length > 0)
          if (hasMedia) {
            cardItems.push(buildCard(handle, video, slideIdx, cardStyle))
            slideItems.push(
              buildDialogSlide(handle, video, slideIdx, totalVideos, this._ctaText, this._ctaAction),
            )
            slideIdx += 1
          } else {
            cardItems.push(buildUnavailable(handle))
          }
        }

        // Close the dialog if open — we're about to replace the slides DOM
        // and any active observer/handlers reference stale nodes.
        if (this.dialog?.open) this.close()
        this.observer?.disconnect()
        this.observer = null
        this.activeIndex = 0

        const grid = this.querySelector('.sai-fsvfrd01__grid')
        if (grid) grid.innerHTML = cardItems.join('')
        if (this.slidesScroller) this.slidesScroller.innerHTML = slideItems.join('')

        // Toggle nav-button visibility based on real-video count (slideIdx).
        // SSR emits both buttons with `hidden`; CSS `__dialog-nav[hidden]`
        // override ensures hidden actually hides on tablet+.
        const showNav = slideIdx > 1
        if (this.prevBtn) this.prevBtn.hidden = !showNav
        if (this.nextBtn) this.nextBtn.hidden = !showNav

        // Re-query DOM refs for the rebuilt cards/slides/products.
        this._refresh()

        // Reveal/hide the carousel arrows + dots for the new card count and
        // reset their disabled/active state to the fresh scroll position.
        this._syncCarouselControls()
      }

      isDesktop() {
        return this.desktopMQ.matches
      }

      getVideo(slide) {
        // Story-aware: prefer the spectrum-video inside the currently active
        // __story-asset. Falls back to the first spectrum-video in the slide
        // for legacy single-asset slides without a `data-active-asset`.
        const idx = slide.dataset.activeAsset || '0'
        const assetEl = slide.querySelector(`[data-asset-idx="${idx}"]`)
        if (assetEl) return assetEl.querySelector('spectrum-video')
        return slide.querySelector('spectrum-video')
      }

      getAllVideos(slide) {
        return slide.querySelectorAll('spectrum-video')
      }

      getActiveVideo() {
        const slide = this.slides[this.activeIndex]
        return slide ? this.getVideo(slide) : null
      }

      neighborIndexes(index, includeSelf = false) {
        const result = []
        if (index - 1 >= 0) result.push(index - 1)
        if (includeSelf) result.push(index)
        if (index + 1 < this.slides.length) result.push(index + 1)
        return result
      }

      applyAudioPreference(video) {
        if (!video) return
        if (this.audioOn) video.unmute()
        else video.mute()
        const inner = video.querySelector('video')
        if (inner) inner.muted = !this.audioOn
      }

      loadSlides(indexes) {
        for (const i of indexes) {
          const slide = this.slides[i]
          if (!slide) continue
          const vid = this.getVideo(slide)
          if (vid) vid.load()
        }
      }

      updatePositions(index) {
        this.activeIndex = index
        for (let i = 0; i < this.slides.length; i++) {
          const slide = this.slides[i]
          const diff = i - index
          const pos = diff === 0 ? 'center' : diff === -1 ? 'left' : diff === 1 ? 'right' : 'hidden'
          slide.dataset.position = pos

          // Story-aware: hidden slides unload ALL asset videos; offscreen-but-
          // adjacent pause ALL asset videos. The center slide's play state is
          // managed by _activateStoryAsset (called by _initStoryForSlide / open).
          const allVids = this.getAllVideos(slide)
          if (pos === 'hidden') {
            for (const v of allVids) v.unload()
          } else if (pos !== 'center') {
            for (const v of allVids) v.pause()
          }
        }
      }

      updateMuteIcons() {
        const isMuted = !this.audioOn
        if (isMuted) {
          this.muteIconMuted.style.display = ''
          this.muteIconSound.style.display = 'none'
          this.muteBtn.setAttribute('aria-label', 'Unmute')
        } else {
          this.muteIconMuted.style.display = 'none'
          this.muteIconSound.style.display = ''
          this.muteBtn.setAttribute('aria-label', 'Mute')
        }
      }

      updateNavButtonsState() {
        // Stories loop — nav always enabled (overflow wraps to first/last).
        if (this.prevBtn) this.prevBtn.disabled = false
        if (this.nextBtn) this.nextBtn.disabled = false
      }

      toggleMute() {
        this.audioOn = !this.audioOn
        // Only the ACTIVE (center) video carries audio. On desktop the dialog shows
        // left/center/right slides at once and their videos autoplay muted, so
        // unmuting every inner video made all three play audio together. Unmute the
        // active asset only; keep the rest muted (they inherit the preference when
        // they become the center via _activateStoryAsset).
        const active = this.getActiveVideo()
        const activeInner = active ? active.querySelector('video') : null
        const allInner = this.dialog.querySelectorAll('.sai-fsvfrd01__story-asset video')
        for (const v of allInner) {
          v.muted = v === activeInner ? !this.audioOn : true
        }
        this.updateMuteIcons()
      }

      // Mark the active mobile slide so CSS shows only its (viewport-fixed)
      // progress bar + product strip — the video track scrolls beneath them.
      _setMobileActive(idx) {
        for (let i = 0; i < this.slides.length; i += 1) {
          if (i === idx) this.slides[i].setAttribute('data-mactive', '1')
          else this.slides[i].removeAttribute('data-mactive')
        }
      }

      setupMobileObserver() {
        if (this.observer) this.observer.disconnect()
        this.observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                const idx = Number.parseInt(entry.target.dataset.index, 10) || 0
                if (idx !== this.activeIndex) {
                  // Mobile swipe analytics: derive direction from index delta.
                  const exitReason = idx > this.activeIndex ? 'next' : 'prev'
                  this._clearStoryState()
                  this._fireView(idx, exitReason)
                  this.activeIndex = idx
                  this._setMobileActive(idx)
                  this.updateNavButtonsState()
                  this._initStoryForSlide(idx)
                  this._setupProductImpressionsObserver()
                }
              }
            }
          },
          { root: this.slidesScroller, threshold: 0.6 },
        )

        for (const slide of this.slides) this.observer.observe(slide)
      }

      open(index) {
        this.activeIndex = index
        this.updateMuteIcons()
        this.updateNavButtonsState()
        // Reset per-dialog product-impression dedup. Each dialog session is
        // independent — re-opening the dialog re-fires product impressions.
        this._impressedProducts = new Set()
        // Fire `shoppable_videos:view_impression` for the initial active video. Starts
        // the view session that pairs with view_duration on next/prev/close.
        this._fireView(index, null)

        if (this.isDesktop()) {
          this.slidesScroller.classList.add('sai-fsvfrd01__dialog-slides--snap')
          for (let i = 0; i < this.slides.length; i++) {
            const diff = i - index
            this.slides[i].dataset.position =
              diff === 0 ? 'center' : diff === -1 ? 'left' : diff === 1 ? 'right' : 'hidden'
          }
        }

        this.dialog.showModal()
        document.body.style.overflow = 'hidden'

        requestAnimationFrame(() => {
          for (const el of this.productScrolls) el.scrollLeft = 0

          if (this.isDesktop()) {
            void this.slidesScroller.offsetHeight
            this.slidesScroller.classList.remove('sai-fsvfrd01__dialog-slides--snap')

            this.loadSlides(this.neighborIndexes(index))
            this.updatePositions(index)
          } else {
            this.loadSlides(this.neighborIndexes(index, true))
            // Mobile scroll is HORIZONTAL (live) — position by scrollLeft.
            this.slidesScroller.scrollLeft = index * this.slidesScroller.clientWidth
            this._setMobileActive(index)
            this.setupMobileObserver()
          }

          // Activate the first asset of the opened slide — drives video play,
          // progress bar start, and per-asset hand-off (ended → next). Replaces
          // the per-platform play() calls above; story logic owns playback.
          this._initStoryForSlide(index)

          // Set up per-product-card IO observer for the active slide. Re-runs
          // on each open() because dialog sessions reset _impressedProducts.
          this._setupProductImpressionsObserver()
        })
      }

      close() {
        // Fire `view_duration` for the active video before tearing down.
        // _pendingExitReason is set by pagehide; otherwise this is 'close'.
        const exitReason = this._pendingExitReason ?? 'close'
        this._fireViewDuration(exitReason)
        this._pendingExitReason = undefined
        // Drop any in-flight story timers / listeners before unloading the
        // assets; otherwise a queued _storyGo could fire mid-teardown.
        this._clearStoryState()

        // Close any open quickshop sheet so it doesn't linger past the
        // dialog. dialogClosing flag suppresses the sheet's resume-video
        // path (would resume on a soon-to-be-hidden surface).
        this.closeQuickshop?.({ dialogClosing: true })

        // If we hoisted the size-guide overlay into our dialog, collapse it so
        // it doesn't re-show on the next open (data-open persists on the node).
        const sgRoot = this.dialog?.querySelector('.sai-fsgfrd01__root')
        if (sgRoot) {
          sgRoot.dataset.open = 'false'
          document.body.style.overflow = ''
        }

        this.observer?.disconnect()
        this._productsObserver?.disconnect()
        this._productsObserver = null
        // Unload every asset video, not just the active one — multi-asset
        // slides can have neighbours buffered.
        for (const slide of this.slides) {
          for (const vid of this.getAllVideos(slide)) vid.unload()
        }
        this.audioOn = false
        this.dialog.close()
        document.body.style.overflow = ''
      }

      go(delta) {
        this._goToSlide(this.activeIndex + delta, 0)
      }

      // Transition from the current slide to slide `rawIndex` (wrapped
      // modulo this.slides.length so first/last loop). `startAsset` picks the
      // story-asset index to activate on the new slide: `0` = first asset
      // (forward navigation, dialog open), `-1` = last asset (backward
      // navigation when overflowing into the previous slide so we land on
      // its final asset, not its first).
      _goToSlide(rawIndex, startAsset) {
        const total = this.slides.length
        if (total === 0) return
        const newIndex = ((rawIndex % total) + total) % total
        if (newIndex === this.activeIndex) return

        const exitReason = rawIndex > this.activeIndex ? 'next' : 'prev'
        this._clearStoryState()
        this._fireView(newIndex, exitReason)
        this.activeIndex = newIndex

        if (this.isDesktop()) {
          this.loadSlides(this.neighborIndexes(newIndex))
          this.updatePositions(newIndex)
        } else {
          this.loadSlides(this.neighborIndexes(newIndex, true))
          // Move the fixed chrome (progress bar + product strip) to the NEW
          // slide IMMEDIATELY — before the smooth scroll. Otherwise data-mactive
          // stays on the outgoing slide (whose bar _clearStoryState just filled
          // to 100%) until the scroll observer belatedly fires, so the user sees
          // a full bar + both slides' chrome overlapping mid-transition.
          this._setMobileActive(newIndex)
          // Mobile scroll is HORIZONTAL (live) — animate by scrollLeft.
          this.slidesScroller.scrollTo({
            left: newIndex * this.slidesScroller.clientWidth,
            behavior: 'smooth',
          })
        }
        this.updateNavButtonsState()
        const slide = this.slides[newIndex]
        let assetIdx = 0
        if (startAsset === -1 && slide) {
          assetIdx = Number.parseInt(slide.dataset.assetCount || '1', 10) - 1
        }
        this._activateStoryAsset(newIndex, assetIdx)
        // Re-arm: observer is scoped to the active slide's product cards;
        // without this, products on slides reached via nav never fire impressions.
        this._setupProductImpressionsObserver()
      }

      // Direct ATC — fired by both the inline single-variant CTA and by the
      // mini-PDP "Add to cart" button. Closes the entire lightbox on success
      // (matches the convention from PR #1750 / Lotto theme).
      //
      // Payload identity rule: the inline CTA carries data-tag-id /
      // data-product-id on the button itself; the mini-PDP qs-atc button
      // lives inside the bottom-sheet (no product-card ancestor) so we fall
      // back to qsState.tag for those fields.
      performAtc(variantId, quantity, btn) {
        const original = btn ? btn.dataset.original || btn.textContent : ''
        const card = btn?.closest?.('.sai-fsvfrd01__product-card')
        const payload = {
          video_id: this._activeVideoHandle(),
          tag_id: btn?.dataset?.tagId ?? card?.dataset?.tagId ?? this.qsState.tag?.tag_id,
          product_id:
            btn?.dataset?.productId ??
            card?.dataset?.productId ??
            this.qsState.tag?.product_id ??
            this.qsState.tag?.id,
          variant_id: variantId,
          quantity,
        }
        // Intent — fires regardless of cart-add outcome so an add_to_cart
        // without a matching added_to_cart reveals failures.
        this._track(`${FEATURE_SLUG}:add_to_cart`, payload)
        this._emit(`${FEATURE_SLUG}:add_to_cart`, payload)

        if (btn) {
          btn.disabled = true
          btn.textContent = 'Adding…'
        }

        const finalize = (ok) => {
          if (ok) {
            this._track(`${FEATURE_SLUG}:added_to_cart`, payload)
            this._emit(`${FEATURE_SLUG}:added_to_cart`, payload)
            // Close sheet + dialog so the theme's cart-drawer (auto-opened by
            // Spectrum.cart.addAndOpen) can take focus.
            this.closeQuickshop({ dialogClosing: true })
            this.close()
          } else if (btn) {
            btn.disabled = false
            btn.textContent = original
          }
        }

        const sdk = window.Spectrum?.cart
        if (sdk && typeof sdk.addAndOpen === 'function') {
          // Pass variantId through verbatim — Shopify's AJAX cart accepts
          // both numeric and string variant IDs and the Spectrum SDK
          // normalises them. Coercing via Number() corrupts non-numeric IDs
          // (test-only or otherwise) into NaN.
          sdk
            .addAndOpen({ id: variantId, quantity }, { sourceId: `spectrum-${SNIPPET_ID}` })
            .then((res) => finalize(res && res.ok !== false))
            .catch(() => finalize(false))
        } else {
          finalize(false)
        }
      }

      // ── Quickshop (mini-PDP bottom sheet) ───────────────────────────────

      // Resolve a product tag on the active slide by tag_id. The cta-quickshop
      // button stamps `data-tag-id` from the pool; we look the tag back up so
      // the bottom sheet populates with the SPECIFIC product the user tapped
      // (not the first in the strip). If tagId is missing, default to the
      // first tag — defensive for legacy markup.
      _findTagOnCurrentSlide(tagId) {
        const handle = this.slides[this.activeIndex]?.dataset.handle
        if (!handle) return null
        const tags = this._pool[handle]?.product_tags
        if (!Array.isArray(tags) || tags.length === 0) return null
        if (tagId == null) return tags[0]
        return tags.find((t) => String(t?.tag_id) === String(tagId)) || null
      }

      // Re-paint the pill grid + header (image / price / compare-at / atc)
      // based on qsState. Called after populate AND on every pill click.
      applyQsSelection(tag) {
        if (!tag || !this.quickshopEl) return
        const variant = findVariantByOptions(tag, this.qsState.optionValues)
        this.qsState.variantId = variant ? String(variant.id) : ''

        // Repaint each pill — selected/disabled state.
        const groups = this.quickshopEl.querySelectorAll('[data-qs-pills]')
        for (const grp of groups) {
          const optName = grp.dataset.qsPills
          const pills = grp.querySelectorAll('.sai-fsvfrd01__quickshop-pill')
          for (const pill of pills) {
            const value = pill.dataset.optionValue
            const selected = this.qsState.optionValues[optName] === value
            pill.classList.toggle('sai-fsvfrd01__quickshop-pill--selected', selected)
            pill.setAttribute('aria-checked', selected ? 'true' : 'false')
            const available = isOptionValueAvailable(tag, optName, value, this.qsState.optionValues)
            pill.classList.toggle('sai-fsvfrd01__quickshop-pill--oos', !available)
            if (!available) {
              pill.setAttribute('aria-label', `${value} — out of stock`)
              pill.setAttribute('aria-disabled', 'true')
            } else {
              pill.removeAttribute('aria-label')
              pill.removeAttribute('aria-disabled')
            }
          }
          const selectedLabel = grp.parentElement?.querySelector('[data-qs-selected]')
          if (selectedLabel) selectedLabel.textContent = this.qsState.optionValues[optName] || ''
        }

        // Header thumbnail prefers the matched variant's featured_image.
        const img = this.quickshopEl.querySelector('[data-qs-image]')
        if (img) {
          const v_img = variant?.featured_image
          img.src = v_img && v_img !== '' ? v_img : tag.thumb_url || ''
          img.alt = tag.display_name || ''
        }

        const price = this.quickshopEl.querySelector('[data-qs-price]')
        if (price)
          price.textContent = (variant?.price ?? tag.variant_price ?? tag.product_price) || ''

        const compare = this.quickshopEl.querySelector('[data-qs-compare]')
        if (compare) {
          if (tag.compare_at_price) {
            compare.textContent = tag.compare_at_price
            compare.hidden = false
          } else {
            compare.textContent = ''
            compare.hidden = true
          }
        }

        // Discount chip — % off, computed from the displayed sale price vs the
        // compare-at. Prices are money-formatted strings; parseMoney strips the
        // currency/grouping so this holds for ₹ and $ alike.
        const discount = this.quickshopEl.querySelector('[data-qs-discount]')
        if (discount) {
          const now = parseMoney(variant?.price ?? tag.variant_price ?? tag.product_price)
          const was = parseMoney(tag.compare_at_price)
          const pct = was > 0 && now > 0 && was > now ? Math.round(((was - now) / was) * 100) : 0
          if (pct > 0) {
            discount.textContent = `${pct}% OFF`
            discount.hidden = false
          } else {
            discount.textContent = ''
            discount.hidden = true
          }
        }

        const atc = this.quickshopEl.querySelector('[data-action="qs-atc"]')
        if (atc) atc.disabled = !this.qsState.variantId || !variant?.available
      }

      populateQuickshop(tag) {
        if (!this.quickshopEl || !tag) return
        this.qsState.tag = tag
        this.qsState.handle = tag.handle || ''

        // Static header bits — title text + view-product link don't depend
        // on the selected variant.
        const productHref = tag.cta_url || tag.product_url || '#'
        const title = this.quickshopEl.querySelector('[data-qs-title]')
        if (title) {
          title.textContent = tag.display_name || ''
          title.href = productHref
        }
        const viewLink = this.quickshopEl.querySelector('[data-qs-view]')
        if (viewLink) {
          viewLink.href = productHref
          if (tag.tag_id != null) viewLink.dataset.tagId = String(tag.tag_id)
          else delete viewLink.dataset.tagId
          if (tag.product_id != null) viewLink.dataset.productId = String(tag.product_id)
          else delete viewLink.dataset.productId
        }

        // Render option groups. When the pool didn't carry options[] (e.g. a
        // single-Default-Title variant), skip rendering — applyQsSelection()
        // can still resolve the variant via positional fallback.
        const optionsContainer = this.quickshopEl.querySelector('[data-qs-options]')
        const opts = Array.isArray(tag.options) ? tag.options : []
        const meaningful =
          opts.length > 0 && !(opts.length === 1 && opts[0].values?.[0]?.name === 'Default Title')
        this.qsState.hasOptions = meaningful
        if (optionsContainer) optionsContainer.innerHTML = ''

        const initial = pickInitialVariant(tag)
        this.qsState.optionValues = optionValuesFromVariant(tag, initial)

        if (meaningful && optionsContainer) {
          for (const opt of opts) {
            const group = document.createElement('div')
            group.className = 'sai-fsvfrd01__quickshop-group'
            const head = document.createElement('div')
            head.className = 'sai-fsvfrd01__quickshop-group-head'
            head.innerHTML = `
              <span class="sai-fsvfrd01__quickshop-group-name">${opt.name}:</span>
              <span class="sai-fsvfrd01__quickshop-group-selected" data-qs-selected></span>
            `
            group.appendChild(head)
            const pillsRow = document.createElement('div')
            pillsRow.className = 'sai-fsvfrd01__quickshop-group-pills'
            pillsRow.dataset.qsPills = opt.name
            pillsRow.setAttribute('role', 'radiogroup')
            pillsRow.setAttribute('aria-label', opt.name)
            for (const ov of opt.values || []) {
              pillsRow.appendChild(buildQuickshopPill(opt, ov))
            }
            group.appendChild(pillsRow)
            optionsContainer.appendChild(group)
          }
        }

        // Size Guide row — shown only when a size-type option is present AND
        // the page actually has a size-guide snippet to open, so the button is
        // never a dead no-op. It opens that snippet; it never navigates.
        const sizeGuide = this.quickshopEl.querySelector('[data-qs-sizeguide]')
        if (sizeGuide) {
          const hasSize = opts.some((o) => /size/i.test(o?.name || ''))
          const hasGuide = !!document.querySelector('sai-fsgfrd01')
          sizeGuide.hidden = !(hasSize && hasGuide)
        }

        // ATC label baseline — applyQsSelection only flips disabled.
        const atc = this.quickshopEl.querySelector('[data-action="qs-atc"]')
        if (atc) {
          atc.textContent = this._ctaText || 'Add to cart'
          atc.dataset.original = this._ctaText || 'Add to cart'
        }

        this.applyQsSelection(tag)
      }

      openQuickshop(tag) {
        if (!this.quickshopEl || !tag) return
        if (this._qsCloseTimer) {
          clearTimeout(this._qsCloseTimer)
          this._qsCloseTimer = null
        }
        this.populateQuickshop(tag)
        // Pause the active video while the sheet is open.
        const active = this.getActiveVideo()
        if (active) active.pause?.()

        this.quickshopEl.hidden = false
        this.quickshopEl.setAttribute('aria-hidden', 'false')
        // Hide the drawer's own product strip so the sheet doesn't collide
        // with it (live replaces the card with the sheet). Restored on close.
        this.dialog?.classList.add('sai-fsvfrd01__dialog--qs-open')
        // Force reflow so the transform transition triggers.
        void this.quickshopEl.offsetHeight
        this.quickshopEl.classList.add('sai-fsvfrd01__quickshop--open')
      }

      closeQuickshop(opts) {
        if (!this.quickshopEl) return
        if (!this.quickshopEl.classList.contains('sai-fsvfrd01__quickshop--open')) return
        this.quickshopEl.classList.remove('sai-fsvfrd01__quickshop--open')
        this.quickshopEl.setAttribute('aria-hidden', 'true')
        this.dialog?.classList.remove('sai-fsvfrd01__dialog--qs-open')
        this._qsCloseTimer = setTimeout(() => {
          if (!this.quickshopEl.classList.contains('sai-fsvfrd01__quickshop--open')) {
            this.quickshopEl.hidden = true
          }
          this._qsCloseTimer = null
        }, 280)
        this.qsState.variantId = ''
        this.qsState.handle = ''
        this.qsState.tag = null
        this.qsState.optionValues = {}

        // Resume playback unless we're closing because the dialog itself is
        // closing (would resume a video on a soon-to-be-hidden surface).
        if (!opts || !opts.dialogClosing) {
          const active = this.getActiveVideo()
          if (active && this.dialog?.open) active.play?.()
        }
      }

      // ── Analytics helpers ──────────────────────────────────────────────

      /** Handle of the active dialog slide (used for view / view_duration / ATC). */
      _activeVideoHandle() {
        return this.slides[this.activeIndex]?.dataset.handle
      }

      /**
       * Fire `shoppable_videos:view_impression` for a newly-active video. If a session
       * already exists for the previous active video, fire its `view_duration`
       * first with the supplied `exitReason`. Pair semantics:
       *   - on dialog open: exitReason=null (no prior session)
       *   - on go(±1): exitReason='next' | 'prev'
       *   - on mobile-IO swap: exitReason='next' | 'prev' (derived from delta)
       */
      _fireView(index, exitReason) {
        const handle = this.slides[index]?.dataset.handle
        if (!handle) return
        if (this._viewSession && exitReason) {
          this._fireViewDuration(exitReason)
        }
        // Start a new session — wall-clock time, looped flag toggled on
        // the active video's `ended` event.
        const video = this._slideVideo(index)
        const session = {
          videoId: handle,
          startedAt: Date.now(),
          looped: false,
          video: video || null,
          onEnded: null,
        }
        if (video) {
          session.onEnded = () => {
            session.looped = true
          }
          video.addEventListener('ended', session.onEnded)
        }
        this._viewSession = session
        this._track(`${FEATURE_SLUG}:view_impression`, { video_id: handle })
      }

      /**
       * Fire `shoppable_videos:view_duration` for the active session and clear
       * it. No-op if no session is active. Idempotent across calls.
       */
      _fireViewDuration(exitReason) {
        const session = this._viewSession
        if (!session) return
        // Detach the ended listener so the next session starts clean.
        if (session.video && session.onEnded) {
          session.video.removeEventListener('ended', session.onEnded)
        }
        const payload = {
          video_id: session.videoId,
          duration_ms: Date.now() - session.startedAt,
          loop: session.looped === true,
          exit_reason: exitReason,
        }
        this._viewSession = null
        this._track(`${FEATURE_SLUG}:view_duration`, payload)
      }

      /** Look up the <spectrum-video> element for a given slide index. */
      _slideVideo(index) {
        const slide = this.slides[index]
        return slide ? this.getVideo(slide) : null
      }

      /**
       * Set up the list-level IntersectionObserver. Fires
       * `shoppable_videos:list_impression` once per page-load when the
       * `<sai-fsvfrd01>` host element hits ≥50% viewport. Idempotent —
       * setting up twice (e.g. on re-applyVariant) is safe.
       */
      _setupListImpressionObserver() {
        if (this._impressedListFired) return
        if (this._listObserver) return
        if (typeof IntersectionObserver === 'undefined') return
        this._listObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                this._fireListImpression()
                this._listObserver?.disconnect()
                this._listObserver = null
                return
              }
            }
          },
          { threshold: 0.5 },
        )
        this._listObserver.observe(this)
      }

      _fireListImpression() {
        if (this._impressedListFired) return
        this._impressedListFired = true
        const visibleVideoCount = this._countVisibleCards()
        this._track(`${FEATURE_SLUG}:list_impression`, {
          list_layout: this._listLayout,
          total_videos_in_list: this._refIds.length,
          visible_video_count: visibleVideoCount,
        })
      }

      /** Count cards currently visible (≥50% in viewport) via bounding-rect. */
      _countVisibleCards() {
        const viewportHeight = window.innerHeight || 0
        const viewportWidth = window.innerWidth || 0
        let count = 0
        for (const card of this.cards) {
          const rect = card.getBoundingClientRect()
          if (rect.width === 0 || rect.height === 0) continue
          const visibleX = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0))
          const visibleY = Math.max(
            0,
            Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
          )
          const visibleArea = visibleX * visibleY
          const totalArea = rect.width * rect.height
          if (visibleArea / totalArea >= 0.5) count += 1
        }
        return count
      }

      /**
       * Per-card IntersectionObserver — fires
       * `shoppable_videos:preview_impression` once per card per page-load.
       * Re-bound on every applyVariant rebuild via _refresh; the
       * _impressedCards Set persists across rebuilds so previously-fired
       * cards don't re-fire when they re-enter view.
       */
      _setupCardImpressionsObserver() {
        if (typeof IntersectionObserver === 'undefined') return
        this._cardsObserver?.disconnect()
        this._cardsObserver = null
        // Race guard: don't set up the observer until analytics handles are
        // installed. Otherwise an above-the-fold card visible at page-load
        // could trigger the IO callback with `this._track === noopTrack`,
        // silently dropping the impression event AND polluting the dedup
        // set so the later real track never fires it.
        if (!this._analyticsReady) return
        if (!this.cards.length) return
        this._cardsObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue
              const card = entry.target
              const handle = card.dataset.handle
              if (!handle || this._impressedCards.has(handle)) continue
              this._impressedCards.add(handle)
              const poolEntry = this._pool[handle]
              const tagged_product_count = Array.isArray(poolEntry?.product_tags)
                ? poolEntry.product_tags.length
                : 0
              this._track(`${FEATURE_SLUG}:preview_impression`, {
                video_id: handle,
                tagged_product_count,
              })
              this._cardsObserver?.unobserve(card)
            }
          },
          { threshold: 0.5 },
        )
        for (const card of this.cards) {
          if (!this._impressedCards.has(card.dataset.handle || '')) {
            this._cardsObserver.observe(card)
          }
        }
      }

      /**
       * Per-product-card IntersectionObserver inside the active dialog slide.
       * Fires `shoppable_videos:product_impression` once per (video_id, tag_id)
       * per dialog session. The dedup set is reset in open().
       */
      _setupProductImpressionsObserver() {
        if (typeof IntersectionObserver === 'undefined') return
        this._productsObserver?.disconnect()
        this._productsObserver = null
        const activeSlide = this.slides[this.activeIndex]
        if (!activeSlide) return
        const productCards = activeSlide.querySelectorAll('.sai-fsvfrd01__product-card')
        if (!productCards.length) return
        const videoId = activeSlide.dataset.handle
        this._productsObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue
              const card = entry.target
              const tagId = card.dataset.tagId
              const productId = card.dataset.productId
              if (!tagId) continue
              const key = `${videoId}:${tagId}`
              if (this._impressedProducts.has(key)) continue
              this._impressedProducts.add(key)
              this._track(`${FEATURE_SLUG}:product_impression`, {
                video_id: videoId,
                tag_id: tagId,
                product_id: productId,
              })
              this._productsObserver?.unobserve(card)
            }
          },
          { threshold: 0.5 },
        )
        for (const card of productCards) {
          this._productsObserver.observe(card)
        }
      }

      /**
       * Fire `shoppable_videos:product_click` for a product-card child element
       * with `data-action="product-click"`. Resolves tag_id/product_id from
       * the closest .sai-fsvfrd01__product-card ancestor (anchors in the
       * card don't carry IDs themselves; the wrapping div does).
       */
      _fireProductClick(actionEl) {
        const card = actionEl.closest('.sai-fsvfrd01__product-card')
        if (!card) return
        const activeVideo = this.getActiveVideo()
        const payload = {
          video_id: this._activeVideoHandle(),
          tag_id: card.dataset.tagId,
          product_id: card.dataset.productId,
          video_progress_seconds: activeVideo?.currentTime ?? 0,
        }
        this._track(`${FEATURE_SLUG}:product_click`, payload)
      }

      /**
       * Debounced grid-scroll handler — fires `shoppable_videos:list_scroll_elem_clicked`
       * once per discrete swipe gesture with `scroll_direction` derived from
       * the delta vs the gesture start position.
       */
      // ── Carousel controls (header arrows + mobile dots/pager) ──
      // The card strip is the scroll container; arrows nudge it by one card
      // width and the dots reflect the nearest card. Purely presentational —
      // no analytics; list_scroll is emitted by _onGridScroll regardless of
      // whether the scroll came from a swipe or an arrow.

      _cardStep() {
        // One card width + the flex column-gap, so a nudge lands the next card
        // flush at the strip's start edge on both breakpoints.
        if (!this.gridEl) return 0
        const card = this.gridEl.querySelector('.sai-fsvfrd01__card')
        if (!card) return 0
        const gap = parseFloat(getComputedStyle(this.gridEl).columnGap) || 0
        return card.getBoundingClientRect().width + gap
      }

      _scrollCarousel(dir) {
        const step = this._cardStep()
        if (!this.gridEl || step <= 0) return
        this.gridEl.scrollBy({ left: dir * step, behavior: 'smooth' })
      }

      // Share the current slide's product URL via the Web Share API, falling
      // back to clipboard copy where unsupported. Never throws — a rejected
      // share (user cancel) or a missing API is a silent no-op.
      _shareCurrent() {
        let url = ''
        const slide = this.slidesScroller?.querySelector(
          `.sai-fsvfrd01__dialog-slide[data-index="${this.activeIndex}"]`,
        )
        const link = slide?.querySelector('[data-action="product-click"]')
        const href = link?.getAttribute('href') || ''
        if (href && href !== '#') {
          url = href.startsWith('/') ? location.origin + href : href
        }
        if (!url) url = location.href
        const title = document.title || 'Frido'
        if (typeof navigator !== 'undefined' && navigator.share) {
          navigator.share({ title, url }).catch(() => {})
        } else if (navigator?.clipboard?.writeText) {
          navigator.clipboard.writeText(url).catch(() => {})
        }
      }

      // Open the page's size-guide snippet (sai-fsgfrd01) OVER the mini-PDP,
      // without closing the drawer. fsgfrd01 renders a position:fixed overlay
      // portaled to <body>; a <body> overlay sits UNDER our top-layer showModal
      // <dialog>, so we instead move that overlay INTO our dialog (same top
      // layer) and raise it above the quickshop panel. Its own close/backdrop
      // handlers are bound to the root, so closing it returns to the mini-PDP.
      // No-ops when no size guide is on the page. NEVER navigates.
      _openSizeGuide() {
        if (!this.dialog) return
        this._track(`${FEATURE_SLUG}:sizeguide_open_elem_clicked`, {})
        const root = document.querySelector('.sai-fsgfrd01__root')
        if (!root) {
          // Not rendered yet — fall back to fsgfrd01's own standalone open.
          window.dispatchEvent(new CustomEvent('spectrum:size-guide-open'))
          return
        }
        this.dialog.appendChild(root)
        root.style.zIndex = '40'
        root.dataset.open = 'true'
        document.body.style.overflow = 'hidden'
      }

      // Build one dot per card, then decide control visibility. Called from
      // applyVariant after the grid is rebuilt. Dots are keyed on card count;
      // whether the nav/pager is actually *shown* is decided by overflow in
      // _syncCarouselOverflow (they live inside the pager, hidden with it).
      _syncCarouselControls() {
        const count = Array.isArray(this._refIds) ? this._refIds.length : 0
        if (this.dotsEl) {
          this.dotsEl.innerHTML =
            count > 1
              ? Array.from(
                  { length: count },
                  (_, i) =>
                    `<span class="sai-fsvfrd01__dot${i === 0 ? ' sai-fsvfrd01__dot--active' : ''}"></span>`,
                ).join('')
              : ''
        }
        this._syncCarouselOverflow()
        this._updateCarouselUi()
      }

      // Show prev/next controls only when the rail actually overflows its
      // viewport — multiple cards that all fit on screen have nothing to page
      // through, so the arrows/pager stay hidden. Reading scrollWidth forces
      // layout, so this is accurate right after a rebuild; the ResizeObserver
      // on the grid re-runs it on viewport resize and when an initially
      // collapsed section first lays out (clientWidth 0 → real width).
      _syncCarouselOverflow() {
        const count = Array.isArray(this._refIds) ? this._refIds.length : 0
        const overflows =
          !!this.gridEl && this.gridEl.scrollWidth > this.gridEl.clientWidth + 1
        const show = count > 1 && overflows
        if (this.navEl) this.navEl.hidden = !show
        if (this.pagerEl) this.pagerEl.hidden = !show
      }

      // Disable arrows at the scroll extremes + move the active dot to the
      // nearest card. Cheap; safe to call on every scroll frame.
      _updateCarouselUi() {
        if (!this.gridEl) return
        const { scrollLeft, scrollWidth, clientWidth } = this.gridEl
        const atStart = scrollLeft <= 1
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 1
        for (const b of [this.navPrev, this.pagerPrev]) if (b) b.disabled = atStart
        for (const b of [this.navNext, this.pagerNext]) if (b) b.disabled = atEnd
        if (this.dotsEl && this.dotsEl.children.length) {
          const step = this._cardStep()
          const idx = step > 0 ? Math.round(scrollLeft / step) : 0
          const dots = this.dotsEl.children
          for (let i = 0; i < dots.length; i += 1) {
            dots[i].classList.toggle('sai-fsvfrd01__dot--active', i === idx)
          }
        }
      }

      _onGridScroll() {
        if (!this.gridEl) return
        // Presentational sync runs on every frame (cheap); analytics stays
        // debounced below so a single swipe reports once.
        this._updateCarouselUi()
        if (this._gridScrollDebounceTimer) {
          clearTimeout(this._gridScrollDebounceTimer)
        }
        this._gridScrollDebounceTimer = setTimeout(() => {
          if (!this.gridEl) return
          const current = this.gridEl.scrollLeft
          const delta = current - this._lastGridScrollLeft
          this._lastGridScrollLeft = current
          if (delta === 0) return
          this._track(`${FEATURE_SLUG}:list_scroll_elem_clicked`, {
            scroll_direction: delta > 0 ? 'forward' : 'backward',
          })
        }, 250)
      }
    }
    customElements.define(TAG, SaiShoppableVideos)
  }

  // ── Analytics-handle defaults ──
  // Used until setAnalytics(track, emit) replaces them. Both no-op so the
  // snippet works in standalone contexts (local dev, tests without bind mock).
  function noopTrack(_name, _payload) {}
  function noopEmit(_name, _detail) {}

  // ── Test surface ──
  // Pure helpers only; applyVariant is a method on the custom-element class
  // and is tested via JSDOM <sai-fsvfrd01> instances directly.
  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiJzqlk4g5 = {
      buildCard,
      buildDialogSlide,
      buildListProductCard,
      buildProductCard,
      buildQuickshopPill,
      buildUnavailable,
      findVariantByOptions,
      isOptionValueAvailable,
      optionValuesFromVariant,
      pickInitialVariant,
      readSnippetPool,
    }
  }

  // ── Bind iteration ──
  function bindAllContainers() {
    const snippetApi = window.__spectrumAi?.snippet
    if (!snippetApi || typeof snippetApi.bind !== 'function') return
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )
    for (const node of containers) {
      const handles = snippetApi.bind(
        node,
        ({ variant, entry, pools, variants, currentVariantId }) => {
          const resolvedVariant = variant ?? variants.find((v) => v.variantId === currentVariantId)
          if (!resolvedVariant?.content) return
          const sai = node.querySelector(TAG)
          if (!sai) return
          const pool = readSnippetPool(node)
          const _envPool = pools && pools.shoppable_videos
          const shoppableVideosPool = (_envPool && Object.keys(_envPool).length > 0) ? _envPool : (pool.shoppable_videos ?? {})
          sai.applyVariant(resolvedVariant.content, entry?.content ?? {}, shoppableVideosPool)
        },
      )
      // Pass the analytics handles to the custom element so it can fire
      // events from user-action handlers. Bind() returns the same handles
      // synchronously even if the bind callback hasn't fired yet — the
      // element's handlers safely no-op until applyVariant has run.
      const sai = node.querySelector(TAG)
      if (sai && handles) sai.setAnalytics(handles.track, handles.emit)
    }
  }

  // async <script> may execute before body parse finishes — gate the bind
  // iteration on DOMContentLoaded so we don't miss late-parsed containers.
  // Custom-element registration above runs synchronously and is safe via
  // the upgrade path + connectedCallback's MutationObserver.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }
})()
